import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useLineAuth from '../hooks/useLineAuth';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, collection, query, where, onSnapshot } from 'firebase/firestore';
import '../styles/line.css';

import { db,auth } from '../../firebase/firebase';
import { allocateNextSlot,cancelReservation } from '../../firebase/callFunctions';
import ReservedCard from '../../shared/components/ReservedCard';
import ClosedCard from '../../shared/components/ClosedCard';
import LoadingCard from '../../shared/components/LoadingCard';
import usePublicQueue from '../../shared/hooks/usePublicQueue';

export default function BookLine() {

  // =========================
  // 1) uid（useLineAuth）判定
  // =========================
  const { error, lineUserId, profile } = useLineAuth();

  // =========================
  // 2) ランタイム購読（usePublicQueue）
  // =========================
  const lineAuthResolved = lineUserId !== undefined; // undefined の間は未確定
  const queue = usePublicQueue({
    userId: lineAuthResolved ? (lineUserId || null) : null,
    userIdField: 'lineId',
    enabled: true,           // runtime は常に購読
  });


  const [busyReserve, setBusyReserve] = useState(false);
  const [reserveError, setReserveError] = useState(""); 
  const [resv, setResv] = useState(null);      // { no, slotId, at, ... } が入る
  const [busyCancel, setBusyCancel] = useState(false);
  const [resvReady, setResvReady] = useState(false); // 予約スナップショット初回到着フラグ

  const [authReady, setAuthReady] = useState(false); // Authの初回判定が終わったか

  // Firebase Auth の状態監視（初回発火で authReady=true）
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, () => setAuthReady(true));
    return () => unsub();
  }, []);

  // =========================
  // 3) 自予約ウォッチ（onSnapshot）
  // =========================
  useEffect(() => {
    // ユーザー切替時に初期化
    setResv(null);
    setResvReady(false);

    if (!lineUserId) {
      return;
    }

    // lineId=自分 かつ “待ち状態” のドキュメントを購読
    const q = query(
      collection(db, 'reservation_slots'),
      where('lineId', '==', lineUserId),
      where('isReserved', '==', true),
      where('canceled', '==', false),
      where('isReceptionAccepted', '==', false),
      where('canceledLineAt', '==', null)
      // ※ orderBy は付けずにクライアント側で最小番号を選びます（余計な複合Index回避）
    );

    const unsub = onSnapshot(q, { includeMetadataChanges: true }, (snap) => {
    const fromCache = snap.metadata.fromCache === true;  

      if (snap.empty) {
        // キャッシュ由来の空は無視し、サーバー応答を待つ
        if (fromCache) return;
        setResv(null);
        setResvReady(true);
        return;
      }
      // 同一ユーザーが複数持っていた場合に備え、最小の受付番号を採用
      let best = null;
      snap.forEach(docSnap => {
        const d = docSnap.data();
        if (!best || (d.reservationNumber ?? 1e9) < (best.reservationNumber ?? 1e9)) {
          best = { id: docSnap.id, ...d };
        }
      });
      if (!best) {
        setResv(null);
        setResvReady(true);
        return;
      }
      // 画面で使う形に整形
      const atMs = best.reservedAt?.toMillis ? best.reservedAt.toMillis() : Date.now();
      setResv({
        no: best.reservationNumber,
        slotId: best.id,
        by: lineUserId,
        reservedType: best.reservedType || 'LINE',
        at: atMs,
      });
      setResvReady(true);

    }, (err) => {
      console.error('reservation_slots watch error', err);
      setResv(null);
      setResvReady(true);
    });

    return () => unsub();
  }, [lineUserId]);

  //オンライン受付状態を取得
  // useEffect(() => {
  //   const ref = doc(db, 'admin', 'runtime'); // 例：admin/runtime に集約
  //   const unsub = onSnapshot(ref, (snap) => {
  //     const d = snap.data() || {};
  //     // フィールド名は自由ですが、以下を推奨
  //     setWaitCount(
  //       typeof d.currentWaitCount === 'number' ? d.currentWaitCount : null
  //     );
  //   // 開いている条件: inhouseOn===true かつ onlineReceptionEnabled===true
  //    const inhouseOn = d.inhouseOn === true;
  //    const onlineEnabled = d.onlineOn === true; // 未設定は false 扱い
  //    setIsOnlineOpen(inhouseOn && onlineEnabled);
      
  //   });
  //   return () => unsub();
  // }, []);
  // （待ち人数／オンライン開閉）は usePublicQueue が購読するためここでの個別購読は不要

  // ---- 予約ボタン ----
  const handleReserve = async () => {

    if (!lineUserId) return; // 二重防御（UIでdisabledにしているが念のため）
    if (busyReserve) return;
    setBusyReserve(true);

    // console.log('allocateNextSlot start busyReserve=', busyReserve);
    try {
      const data = await allocateNextSlot({ reservedType: "LINE" });
      if (!data?.ok) throw new Error("番号の確保に失敗しました。");

      // //LINE メッセージ送信（待たせたくないなら await しない）
      // sendReservationMessageByLine({ slotId: data.id }).catch(err => console.warn('LINE push failed:', err));

    } catch (e) {
      // Cloud Functions の HttpsError を人間向けに整形
      const code = e?.code; // "failed-precondition" | "resource-exhausted" | "aborted" など
      let msg =
        e?.message ||
        "受付番号の確保に失敗しました。時間をおいて再度お試しください。";
      if (code === "failed-precondition") {
        msg = "現在オンライン受付を停止中です。時間をおいて再度お試しください。";
      } else if (code === "resource-exhausted") {
        msg = "空き番号がありません。しばらくしてから再試行してください。";
      } else if (code === "aborted") {
        msg = "混み合っています。もう一度お試しください。";
      }
      setReserveError(msg);
      alert(msg); // モバイル検証用にアラートも出す（不要なら削除）
    } finally {
      setBusyReserve(false);
    }
  };

  // ---- 予約キャンセルボタン ----
  const handleInlineCancel = async () => {
    if (!resv) return;
    setBusyCancel(true);
    try {
      await cancelReservation({
        slotId: resv.slotId,
        reservationNumber: resv.no,
        canceledBy: "LINE",
      });
      setResv(null); // ← これで予約カードを閉じて通常画面に戻る
    } catch (e) {
      alert(e?.message || '予約の取消に失敗しました。');
    } finally {
      setBusyCancel(false);
    }
  };

  // ---- エラー画面 ----
  if (error) {
    return (
      <div className="line-container">
        <div className="line-card">
          <h1 className="line-title">認証エラー</h1>
          <p className="line-desc">LINEからアクセスし直してください。</p>
        </div>
      </div>
    );
  }



  /**
   * 初回ロード待ち
   */
  if (
    !authReady ||                 // Firebase Auth 未確定
    !queue.runtimeReady ||        // ランタイム未確定
    (authReady && !lineUserId) || // サインイン(uid)がまだ来ていない
    (lineUserId && !resvReady)    // 予約スナップショット未確定
  ) {

    return (
      <div className="line-container">
        <LoadingCard />
      </div>
    );
  }


  // ---- 予約存在時表示 ----
  if (resv) {
    return (
      <div className="line-container">
        <ReservedCard
          resv={resv} 
          myAheadCount={queue.myAheadCount}   // ← 自分の前にいる人数を渡す
          displayName={profile?.displayName}
          onCancel={handleInlineCancel}
          cancelBusy={busyCancel}
        />
      </div>
    );
  }

  // ---- オンライン停止時 ----
if (!queue.isOnlineOpen) {
    return (
      <div className="line-container">
        <ClosedCard />
      </div>
    );

  }

  // ---- オンライン時の初期表示 ----
  return (
    <div className="line-container">
      <div className="line-card">
        <div className="line-wait">
          <div className="line-wait-caption">現在の待ち人数</div>
          <div className="line-wait-count">
            {queue.waitCount == null ? '—' : `${queue.waitCount}人`}
          </div>
        </div>

        <p className="line-desc">
          院内でも診察の順番受付を承っています。来院時には必ず受付番号をスタッフまでお知らせください。この受付番号は中待合に入るまでの順番になります。お呼びする順番は前後する場合があります。
        </p>

        <button
          className={`line-primary-btn line-primary-btn--xl`}
          onClick={handleReserve}
          disabled={!(authReady && !!lineUserId) || busyReserve || !!resv}
        >
          { !authReady
          ? '認証を確認しています…'
          : !lineUserId
            ? 'LINE認証中です'
            : busyReserve
              ? '予約番号を確保しています…'
              : '受付番号予約' }
        </button>


        {reserveError && (
          <p className="line-error" role="alert" aria-live="assertive">{reserveError}</p>
        )}

        {profile?.displayName && (
          <div className="line-welcome">ようこそ、{profile.displayName} さん</div>
        )}

        <div className="line-links">
          <Link to="https://liff.line.me/1654106088-gLDwLw4v" className="text-link">ご予約状況</Link>
          <span className="dot">・</span>
          <Link to="https://liff.line.me/1654106088-ZXJvAvXj" className="text-link">予約一覧</Link>
        </div>
      </div>
    </div>
  );
}
